import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faComment,
  faTimes,
  faPen,
  faArrowLeft,
  faCheck,
  faLock,
  faChevronDown,
  faChevronRight,
  faClipboardList,
  faRobot,
} from '@fortawesome/free-solid-svg-icons';
import useConversations from '../../hooks/useConversations';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../contexts/SocketContext';
import { useMessagesUI } from '../../contexts/MessagesUIContext';
import CustomSelect from '../widgets/CustomSelect';
import ConversationObjectLabel from './ConversationObjectLabel';
import {
  fetchOnlineUserIds,
  buildRecipientOptions,
  formatRecipientOptionLabel,
} from './recipientOptions';
import { isTaskConversation, isAgentConversation, isCompletedAssistantThread, isDismissibleAssistantThread } from './conversationKinds';
import '../../styles/MessagesModal.css';

const formatMessageTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatTaskStatus = (status) => (status === 'closed' ? 'complete' : status);

const ConversationListItem = ({
  conversation,
  selected,
  onSelect,
  onNavigate,
  onlineUserIds,
}) => {
  const otherId = conversation.other_participant?.id;
  const otherIsAgent = isAgentConversation(conversation)
    || conversation.other_participant?.is_system;
  const otherOnline = !otherIsAgent && otherId != null && onlineUserIds?.has(Number(otherId));
  const isGeneral = conversation.object_type === 'general';

  return (
    <button
      type="button"
      className={`messages-list-item ${selected ? 'selected' : ''} ${conversation.is_unread ? 'unread' : ''}`}
      onClick={() => onSelect(conversation.id)}
    >
      <div className="messages-list-item-top">
        {conversation.is_unread && (
          <span className="messages-unread-dot" title="Unread" aria-hidden="true" />
        )}
        <div className="messages-list-item-label">
          {isGeneral && otherOnline && (
            <span className="messages-online-dot" title="Online (just now)" aria-label="Online" />
          )}
          {isGeneral ? (
            <span>
              {conversation.other_participant
                ? (conversation.other_participant.is_system
                  ? conversation.other_participant.name
                  : `${conversation.other_participant.role} - ${conversation.other_participant.name}`)
                : conversation.object_label}
            </span>
          ) : (
            <ConversationObjectLabel
              conversation={conversation}
              onNavigate={onNavigate}
            />
          )}
        </div>
        {((isTaskConversation(conversation) && conversation.status)
          || isCompletedAssistantThread(conversation)) && (
          <span className={`messages-status-chip status-${conversation.status || 'closed'}`}>
            {formatTaskStatus(conversation.status || 'closed')}
          </span>
        )}
      </div>
      {conversation.other_participant && !isGeneral && (
        <div className="messages-list-item-with">
          {otherOnline && (
            <span className="messages-online-dot" title="Online (just now)" aria-label="Online" />
          )}
          with {conversation.other_participant.role} - {conversation.other_participant.name}
        </div>
      )}
      <div className="messages-list-item-preview">{conversation.last_message_preview}</div>
      <div className="messages-list-item-time">
        {formatMessageTime(conversation.last_message_at)}
      </div>
    </button>
  );
};

const SidebarSection = ({
  title,
  icon,
  items,
  collapsed,
  onToggle,
  emptyText,
  selectedId,
  viewMode,
  onSelect,
  onNavigate,
  onlineUserIds,
}) => (
  <div className="messages-sidebar-section">
    <button
      type="button"
      className="messages-sidebar-section-header"
      onClick={onToggle}
      aria-expanded={!collapsed}
    >
      <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronDown} className="messages-section-chevron" />
      {icon && <FontAwesomeIcon icon={icon} />}
      <span>{title}</span>
      <span className="messages-section-count">{items.length}</span>
    </button>
    {!collapsed && (
      <div className="messages-sidebar-section-body">
        {items.length === 0 ? (
          <div className="messages-list-empty messages-section-empty">{emptyText}</div>
        ) : (
          items.map((conversation) => (
            <ConversationListItem
              key={conversation.id}
              conversation={conversation}
              selected={selectedId === conversation.id && viewMode === 'thread'}
              onSelect={onSelect}
              onNavigate={onNavigate}
              onlineUserIds={onlineUserIds}
            />
          ))
        )}
      </div>
    )}
  </div>
);

const MessagesModal = () => {
  const { isOpen, openOptions, closeMessages } = useMessagesUI();
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState('thread');
  const [filterUnread, setFilterUnread] = useState(false);
  const [messages, setMessages] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [composeRecipient, setComposeRecipient] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeRecipients, setComposeRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());
  const [openTasksCollapsed, setOpenTasksCollapsed] = useState(false);
  const [conversationsCollapsed, setConversationsCollapsed] = useState(false);
  const [resolvedCollapsed, setResolvedCollapsed] = useState(true);
  const [assistantWriting, setAssistantWriting] = useState(false);
  const messagesEndRef = useRef(null);
  const threadBodyRef = useRef(null);
  const selectedIdRef = useRef(null);
  const viewModeRef = useRef(viewMode);
  const { hasPermission } = useAuth();
  const { socket } = useSocket();
  const canSendMessages = hasPermission ? hasPermission('/messaging/send', 'Execute') : false;

  const {
    conversations,
    loading,
    canViewMessages,
    fetchConversations,
    fetchConversationDetail,
    replyToConversation,
    sendMessage,
    updateTaskStatus,
    markAsRead,
    fetchEligibleRecipients,
    findDirectConversation,
  } = useConversations();

  selectedIdRef.current = selectedId;
  viewModeRef.current = viewMode;

  const resetState = useCallback(() => {
    setSelectedId(null);
    setViewMode('thread');
    setMessages([]);
    setActiveConversation(null);
    setReplyText('');
    setComposeRecipient('');
    setComposeBody('');
    setComposeRecipients([]);
    setAssistantWriting(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }

    fetchConversations({ limit: 50, unreadOnly: filterUnread }).catch(console.error);

    if (openOptions.compose) {
      setViewMode('compose');
      setSelectedId(null);
    } else if (openOptions.conversationId) {
      setSelectedId(openOptions.conversationId);
      setViewMode('thread');
    }
  }, [isOpen, openOptions, filterUnread, fetchConversations, resetState]);

  // "Just now" online presence for list + compose (Admin Dashboard definition)
  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    const refreshOnline = async () => {
      const ids = await fetchOnlineUserIds();
      if (!cancelled) setOnlineUserIds(ids);
    };

    refreshOnline();
    const interval = setInterval(refreshOnline, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || viewMode !== 'compose' || !canSendMessages) return undefined;

    let cancelled = false;

    const loadRecipients = async () => {
      setLoadingRecipients(true);
      try {
        const [recipientData, onlineIds] = await Promise.all([
          fetchEligibleRecipients(),
          fetchOnlineUserIds(),
        ]);
        if (!cancelled) {
          const humans = buildRecipientOptions(recipientData.recipients || [], onlineIds);
          const options = [];
          if (recipientData.assistant) {
            options.push({
              value: recipientData.assistant.id,
              label: recipientData.assistant.label || 'Clinical Assistant',
              isAssistant: true,
              isOnline: false,
            });
          }
          setComposeRecipients([...options, ...humans]);
        }
      } catch (err) {
        console.error('Failed to load recipients:', err);
      } finally {
        if (!cancelled) {
          setLoadingRecipients(false);
        }
      }
    };

    loadRecipients();

    return () => {
      cancelled = true;
    };
  }, [isOpen, viewMode, canSendMessages, fetchEligibleRecipients]);

  useEffect(() => {
    if (!isOpen || !selectedId || viewMode === 'compose') {
      if (!selectedId) {
        setMessages([]);
        setActiveConversation(null);
      }
      return;
    }

    const loadThread = async () => {
      setLoadingThread(true);
      try {
        const data = await fetchConversationDetail(selectedId);
        setActiveConversation(data.conversation);
        setMessages(data.messages || []);
        setAssistantWriting(false);
        if (data.conversation?.is_unread) {
          await markAsRead(selectedId);
        }
      } catch (err) {
        console.error('Failed to load conversation:', err);
      } finally {
        setLoadingThread(false);
      }
    };

    loadThread();
  }, [isOpen, selectedId, viewMode]);

  const scrollThreadToBottom = useCallback((behavior = 'auto') => {
    const body = threadBodyRef.current;
    if (body) {
      if (typeof body.scrollTo === 'function') {
        body.scrollTo({ top: body.scrollHeight, behavior });
      } else {
        body.scrollTop = body.scrollHeight;
      }
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  useEffect(() => {
    if (!isOpen || loadingThread || !selectedId || viewMode !== 'thread') return undefined;

    const frame = requestAnimationFrame(() => {
      scrollThreadToBottom('auto');
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, selectedId, viewMode, loadingThread, messages, assistantWriting, scrollThreadToBottom]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e) => {
      if (e.key === 'Escape') closeMessages();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeMessages]);

  useEffect(() => {
    if (!isOpen || !socket) return undefined;

    const handleNewMessage = (messageEvent) => {
      const activeId = selectedIdRef.current;
      const isActiveThread =
        viewModeRef.current === 'thread'
        && activeId
        && Number(activeId) === Number(messageEvent.conversationId);

      if (!isActiveThread) return;

      const incoming = {
        id: messageEvent.messageId || `temp-${messageEvent.createdAt}-${messageEvent.senderId}`,
        body: messageEvent.body || messageEvent.preview,
        message_type: messageEvent.messageType || 'user',
        createdAt: messageEvent.createdAt || new Date().toISOString(),
        is_own: false,
        sender: messageEvent.senderName
          ? { id: messageEvent.senderId, name: messageEvent.senderName }
          : null,
      };

      setMessages((prev) => {
        if (messageEvent.messageId && prev.some((m) => m.id === messageEvent.messageId)) {
          return prev;
        }
        return [...prev, incoming];
      });

      if (messageEvent.status !== undefined) {
        setActiveConversation((prev) => (
          prev
            ? {
                ...prev,
                status: messageEvent.status,
                kind: messageEvent.kind || prev.kind,
              }
            : prev
        ));
      }

      if (messageEvent.kind === 'agent' || messageEvent.senderId) {
        setAssistantWriting(false);
      }

      markAsRead(activeId).catch(console.error);
    };

    socket.on('new_message', handleNewMessage);
    return () => socket.off('new_message', handleNewMessage);
  }, [isOpen, socket, markAsRead]);

  const sectionedLists = useMemo(() => {
    const openTasks = [];
    const directConversations = [];
    const resolvedClosed = [];

    conversations.forEach((c) => {
      if (isCompletedAssistantThread(c)) {
        resolvedClosed.push(c);
      } else if (isTaskConversation(c)) {
        if (c.status === 'resolved' || c.status === 'closed') {
          resolvedClosed.push(c);
        } else {
          openTasks.push(c);
        }
      } else {
        directConversations.push(c);
      }
    });

    return { openTasks, directConversations, resolvedClosed };
  }, [conversations]);

  const appendResultMessages = (result) => {
    setMessages((prev) => {
      const next = [...prev];
      (result.system_messages || []).forEach((sysMsg) => {
        if (!next.some((m) => m.id === sysMsg.id)) next.push(sysMsg);
      });
      if (result.message && !next.some((m) => m.id === result.message.id)) {
        next.push(result.message);
      }
      return next;
    });
    if (result.conversation) {
      setActiveConversation(result.conversation);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedId) return;

    setSending(true);
    try {
      const result = await replyToConversation(selectedId, replyText);
      appendResultMessages(result);
      setReplyText('');
      if (result.agent_pending) setAssistantWriting(true);
      fetchConversations({ limit: 50, unreadOnly: filterUnread }).catch(console.error);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleResolveTask = async () => {
    if (!selectedId) return;
    setStatusUpdating(true);
    try {
      const result = await updateTaskStatus(selectedId, 'resolved');
      appendResultMessages(result);
      fetchConversations({ limit: 50, unreadOnly: filterUnread }).catch(console.error);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to resolve task');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCloseTask = async () => {
    if (!selectedId) return;
    setStatusUpdating(true);
    try {
      const result = await updateTaskStatus(selectedId, 'closed');
      appendResultMessages(result);
      fetchConversations({ limit: 50, unreadOnly: filterUnread }).catch(console.error);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark task as complete');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleComposeSend = async (e) => {
    e.preventDefault();
    if (!composeRecipient || !composeBody.trim()) return;

    setSending(true);
    try {
      const result = await sendMessage({
        recipientId: parseInt(composeRecipient, 10),
        body: composeBody,
      });
      setComposeRecipient('');
      setComposeBody('');
      setViewMode('thread');
      setSelectedId(result.conversation.id);
      if (result.agent_pending) setAssistantWriting(true);
      fetchConversations({ limit: 50, unreadOnly: filterUnread }).catch(console.error);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const openExistingConversation = useCallback((conversationId, draftText = '') => {
    setComposeRecipient('');
    setComposeBody('');
    setViewMode('thread');
    setSelectedId(conversationId);
    if (draftText.trim()) {
      setReplyText(draftText);
    }
  }, []);

  const handleComposeRecipientChange = async (name, value) => {
    setComposeRecipient(value);
    if (!value) return;

    const recipientId = parseInt(value, 10);
    const localMatch = conversations.find(
      (c) =>
        c.object_type === 'general'
        && Number(c.other_participant?.id) === recipientId
    );

    if (localMatch) {
      openExistingConversation(localMatch.id, composeBody);
      return;
    }

    try {
      const existing = await findDirectConversation(recipientId);
      if (existing?.id) {
        openExistingConversation(existing.id, composeBody);
      }
    } catch (err) {
      console.error('Failed to look up existing conversation:', err);
    }
  };

  const handleSelectConversation = (conversationId) => {
    setViewMode('thread');
    setSelectedId(conversationId);
  };

  const handleNewMessage = () => {
    setViewMode('compose');
    setSelectedId(null);
    setMessages([]);
    setActiveConversation(null);
  };

  const handleBackToList = () => {
    setViewMode('thread');
    setSelectedId(null);
    setMessages([]);
    setActiveConversation(null);
  };

  if (!isOpen || !canViewMessages) return null;

  const showRightPane = selectedId || viewMode === 'compose';
  const isGeneralConversation = activeConversation?.object_type === 'general';
  const isAssistantThread = isAgentConversation(activeConversation);
  const isActiveTask = isTaskConversation(activeConversation);
  const taskStatus = activeConversation?.status || 'open';
  const showResolve =
    canSendMessages
    && isActiveTask
    && !activeConversation?.is_creator
    && taskStatus === 'open';
  const showClose =
    canSendMessages
    && (
      isDismissibleAssistantThread(activeConversation)
      || (
        isActiveTask
        && activeConversation?.is_creator
        && (taskStatus === 'open' || taskStatus === 'resolved')
      )
    );
  const isClosedAssistant = isCompletedAssistantThread(activeConversation);

  return (
    <div
      className="messages-modal-overlay"
      onClick={closeMessages}
      role="presentation"
    >
      <div
        className={`messages-modal ${showRightPane ? 'messages-modal--detail-open' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Messages"
      >
        <div className="messages-modal-header">
          <h2>
            <FontAwesomeIcon icon={faComment} /> Messages
          </h2>
          <div className="messages-modal-header-actions">
            {canSendMessages && (
              <button type="button" className="messages-new-btn" onClick={handleNewMessage}>
                <FontAwesomeIcon icon={faPen} /> New message
              </button>
            )}
            <button
              type="button"
              className="messages-modal-close"
              onClick={closeMessages}
              aria-label="Close messages"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        <div className="messages-modal-body messages-page">
          <div className="messages-sidebar">
            <div className="messages-sidebar-header">
              <div className="messages-filters">
                <button
                  type="button"
                  className={!filterUnread ? 'active' : ''}
                  onClick={() => setFilterUnread(false)}
                >
                  All
                </button>
                <button
                  type="button"
                  className={filterUnread ? 'active' : ''}
                  onClick={() => setFilterUnread(true)}
                >
                  Unread
                </button>
              </div>
            </div>

            <div className="messages-list">
              {loading && <div className="messages-list-empty">Loading...</div>}
              {!loading && (
                <>
                  <SidebarSection
                    title="Conversations"
                    icon={faComment}
                    items={sectionedLists.directConversations}
                    collapsed={conversationsCollapsed}
                    onToggle={() => setConversationsCollapsed((v) => !v)}
                    emptyText="No conversations"
                    selectedId={selectedId}
                    viewMode={viewMode}
                    onSelect={handleSelectConversation}
                    onNavigate={closeMessages}
                    onlineUserIds={onlineUserIds}
                  />
                  <SidebarSection
                    title="Open Tasks"
                    icon={faClipboardList}
                    items={sectionedLists.openTasks}
                    collapsed={openTasksCollapsed}
                    onToggle={() => setOpenTasksCollapsed((v) => !v)}
                    emptyText="No open tasks"
                    selectedId={selectedId}
                    viewMode={viewMode}
                    onSelect={handleSelectConversation}
                    onNavigate={closeMessages}
                    onlineUserIds={onlineUserIds}
                  />
                  <SidebarSection
                    title="Resolved / Complete"
                    icon={faCheck}
                    items={sectionedLists.resolvedClosed}
                    collapsed={resolvedCollapsed}
                    onToggle={() => setResolvedCollapsed((v) => !v)}
                    emptyText="No resolved or completed tasks"
                    selectedId={selectedId}
                    viewMode={viewMode}
                    onSelect={handleSelectConversation}
                    onNavigate={closeMessages}
                    onlineUserIds={onlineUserIds}
                  />
                </>
              )}
            </div>
          </div>

          <div className="messages-thread">
            {viewMode === 'compose' && (
              <>
                <div className="messages-thread-header">
                  <button type="button" className="messages-back-btn" onClick={handleBackToList}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                  </button>
                  <h3>New message</h3>
                </div>
                <form className="messages-compose-form" onSubmit={handleComposeSend}>
                  <div className="form-group">
                    <label htmlFor="compose-recipient">Send to:</label>
                    {loadingRecipients ? (
                      <p>Loading users...</p>
                    ) : (
                      <CustomSelect
                        name="compose-recipient"
                        value={composeRecipient}
                        onChange={handleComposeRecipientChange}
                        options={composeRecipients}
                        placeholder="Search Role - Name..."
                        isClearable
                        isSearchable
                        disabled={loadingRecipients}
                        formatOptionLabel={formatRecipientOptionLabel}
                      />
                    )}
                    <p className="messages-compose-hint">
                      Clinical Assistant is listed first when you can use it. Staff tasks stay in the rest of the list.
                    </p>
                  </div>
                  <div className="form-group">
                    <label htmlFor="compose-body">Message:</label>
                    <textarea
                      id="compose-body"
                      value={composeBody}
                      onChange={(e) => setComposeBody(e.target.value)}
                      placeholder="Type your message..."
                      rows="6"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending || !composeRecipient || !composeBody.trim()}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                    {sending ? ' Sending...' : ' Send'}
                  </button>
                </form>
              </>
            )}

            {viewMode === 'thread' && !selectedId && (
              <div className="messages-thread-empty">
                <FontAwesomeIcon icon={faComment} size="2x" />
                <p>Select a task or conversation, or start a new message</p>
              </div>
            )}

            {viewMode === 'thread' && selectedId && loadingThread && (
              <div className="messages-thread-empty">Loading...</div>
            )}

            {viewMode === 'thread' && selectedId && !loadingThread && activeConversation && (
              <>
                <div className="messages-thread-header">
                  <button type="button" className="messages-back-btn mobile-only" onClick={handleBackToList}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </button>
                  <div>
                    <h3>
                      {isActiveTask && <span className="messages-task-badge">Task</span>}
                      {isAssistantThread && (
                        <span className="messages-task-badge messages-assistant-badge">
                          <FontAwesomeIcon icon={faRobot} /> Assistant
                        </span>
                      )}
                      <ConversationObjectLabel
                        conversation={activeConversation}
                        onNavigate={closeMessages}
                      />
                    </h3>
                    {activeConversation.other_participant && !isGeneralConversation && (
                      <p>
                        with {activeConversation.other_participant.role} - {activeConversation.other_participant.name}
                      </p>
                    )}
                    {isAssistantThread && (
                      <p className="ask-assistant-disclaimer">
                        Drafts only. Confirm important details in the patient chart before acting.
                      </p>
                    )}
                    {isActiveTask && (
                      <p className={`messages-task-status status-${taskStatus}`}>
                        Status: {formatTaskStatus(taskStatus)}
                      </p>
                    )}
                  </div>
                  {activeConversation.object_link && (
                    <Link
                      to={activeConversation.object_link}
                      className="messages-object-link"
                      onClick={closeMessages}
                    >
                      View record
                    </Link>
                  )}
                </div>

                <div className="messages-thread-body" ref={threadBodyRef}>
                  {messages.map((msg) => (
                    msg.message_type === 'system' ? (
                      <div key={msg.id} className="message-system">
                        <span>{msg.body}</span>
                        <time>{formatMessageTime(msg.createdAt)}</time>
                      </div>
                    ) : (
                      <div
                        key={msg.id}
                        className={`message-bubble ${msg.is_own ? 'own' : 'other'}`}
                      >
                        {!msg.is_own && msg.sender && (
                          <div className="message-bubble-sender">{msg.sender.name}</div>
                        )}
                        <div className="message-bubble-text">{msg.body}</div>
                        <div className="message-bubble-time">{formatMessageTime(msg.createdAt)}</div>
                      </div>
                    )
                  ))}
                  {assistantWriting && isAssistantThread && (
                    <div className="message-bubble other message-bubble-pending">
                      <div className="message-bubble-sender">Clinical Assistant</div>
                      <div className="message-bubble-text">Assistant is writing...</div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form className="messages-reply-form" onSubmit={handleSendReply}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={
                      canSendMessages
                        ? (isClosedAssistant
                          ? 'Type a reply to reopen...'
                          : (isAssistantThread
                            ? 'Ask the assistant...'
                            : ((taskStatus === 'closed'
                              || (taskStatus === 'resolved' && activeConversation?.is_creator))
                              ? 'Type a reply to reopen this task...'
                              : 'Type a reply...')))
                        : 'You do not have permission to send messages'
                    }
                    rows="3"
                    disabled={sending || !canSendMessages}
                  />
                  <div className="messages-reply-actions">
                    {showResolve && (
                      <button
                        type="button"
                        className="messages-resolve-btn"
                        onClick={handleResolveTask}
                        disabled={sending || statusUpdating}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                        {statusUpdating ? ' Updating...' : ' Resolved'}
                      </button>
                    )}
                    {showClose && (
                      <button
                        type="button"
                        className="messages-close-task-btn"
                        onClick={handleCloseTask}
                        disabled={sending || statusUpdating}
                      >
                        <FontAwesomeIcon icon={faLock} />
                        {statusUpdating ? ' Updating...' : ' Mark Complete'}
                      </button>
                    )}
                    {canSendMessages && (
                      <button type="submit" disabled={sending || !replyText.trim()}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        {sending ? ' Sending...' : ' Send'}
                      </button>
                    )}
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesModal;
